import React from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import styled, { css } from 'styled-components'
import { Wrapper, Grid, H, Image } from '@farewill/ui'
import {
  GTR,
  CONTENT_OUTER_WIDTH,
  CONTAINER_PADDING_HORIZONTAL_AT_BP,
  CONTAINER_PADDING_VERTICAL_AT_BP,
} from '@farewill/ui/tokens'
import { screenMin } from '@farewill/ui/helpers/responsive'
import { paragraphL } from '@farewill/ui/helpers/text'
import { CLOUDINARY_ROOT_PATH } from '@farewill/ui/components/Image/constants'

import LinkButton from 'components/LinkButton'
import PreventOrphan from 'components/PreventOrphan'
import TrustpilotSummary from 'components/TrustpilotWidget/TrustpilotSummary'
import { WILLS_SIGN_UP_PATH } from 'config'

type HeroImageTransformationParams = {
  small: string
  medium: string
  large: string
  extraLarge: string
}

type HeroProps = {
  assetDirectory: string
  ctaText: string
  imageTransformationParams?: HeroImageTransformationParams
  minHeight?: number
  subtitle?: string
  telephoneWillsEnabled: boolean
  title: string
}

type FormatPathArgs = { ext: string; path: string; width: number }

const IMAGE_MAX_WIDTH_BREAKPOINT = 1695
const DEFAULT_IMAGE_TRANSFORMATION_PARAMS = {
  small: 'ar_1.8,c_fill,f_auto,q_auto,g_faces,w_300',
  medium: 'ar_2.23,c_fill,f_auto,q_auto,g_faces,w_964',
  large: 'ar_1.05,c_fill,f_auto,q_auto,g_faces,h_650',
  extraLarge: 'ar_1.4,c_crop,f_auto,q_auto,g_faces,z_0.9/h_650',
}

const StyledRelativeWrapper = styled(Wrapper)`
  position: relative;
  overflow: hidden;
  ${screenMin.xl`
    max-height: 600px;
  `}
`

const StyledGrid = styled(Grid)<{ $minHeight?: number }>`
  align-items: center;

  ${({ $minHeight }) =>
    $minHeight &&
    css`
      min-height: ${$minHeight}px;
    `}

  /**
   * This essentially re-implements the container styles, we can't use the prop
   * as we only want the grid to behave as a container from L screens.
   */
  ${screenMin.l`
    max-width: ${CONTENT_OUTER_WIDTH};
    padding: ${GTR[CONTAINER_PADDING_VERTICAL_AT_BP.L]} ${
    GTR[CONTAINER_PADDING_HORIZONTAL_AT_BP.L]
  };
    margin: 0 auto;
  `}
`

const StyledImageGridItem = styled(Grid.Item)`
  align-self: stretch;

  ${screenMin.l`
    && {
      /**
       * Allow the image to take up the container vertical padding space so that
       * the image has the same height as the whole hero.
       */
      margin: -${GTR[CONTAINER_PADDING_VERTICAL_AT_BP.L]} 0;
    }
  `}

  /**
   * Once we have exceeded the maximum image width, the image should adhere to
   * the right of the screen
   */
  @media (min-width: ${IMAGE_MAX_WIDTH_BREAKPOINT}px) {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;

    && {
      margin: 0;
    }
  }
`

const StyledSmallImage = styled(Image)`
  clip-path: circle(170% at 50% -148%);

  ${screenMin.m`
    display: none;
  `}
`

const StyledMediumImage = styled(Image)`
  display: none;
  clip-path: circle(210% at 50% -263%);

  ${screenMin.m`
    display: block;
  `}

  ${screenMin.l`
    display: none;
  `}
`

const LARGE_IMAGE_STYLES = `
  clip-path: circle(farthest-side at right 30%);
  height: 100%;
`

const StyledLargeImage = styled(Image)`
  ${LARGE_IMAGE_STYLES}
  display: none;

  ${screenMin.l`
    display: block;
  `}

  ${screenMin.xl`
    display: none;
  `}
`

const StyledExtraLargeImage = styled(Image)`
  ${LARGE_IMAGE_STYLES}
  display: none;

  ${screenMin.xl`
    display: block;
  `}
`

const StyledContentGridItem = styled(Grid.Item)``

const StyledSubtitle = styled.div`
  p {
    ${paragraphL}
  }
`

const Hero = ({
  assetDirectory,
  imageTransformationParams:
    _transformationParams = DEFAULT_IMAGE_TRANSFORMATION_PARAMS,
  title,
  subtitle,
  ctaText,
  minHeight,
  telephoneWillsEnabled,
}: HeroProps): React.ReactElement => {
  const imageTransformationParams = {
    ...DEFAULT_IMAGE_TRANSFORMATION_PARAMS,
    ..._transformationParams,
  }

  const heroImagePath = `com-assets/${assetDirectory}/hero`

  return (
    <StyledRelativeWrapper>
      <StyledGrid gap={0} gapFromL="XL" gapFromXL="XXL" $minHeight={minHeight}>
        <StyledImageGridItem
          span={12}
          spanFromL={6}
          startColumnFromL={7}
          spanFromXL={7}
          startColumnFromXL={6}
        >
          <StyledSmallImage
            formatPath={({ ext, path }: FormatPathArgs) =>
              `${CLOUDINARY_ROOT_PATH}/${imageTransformationParams.small}/${path}.${ext}`
            }
            path={heroImagePath}
            ext="jpg"
            width={300}
            widthFromS={648}
            stretch
          />

          <StyledMediumImage
            formatPath={({ ext, path }: FormatPathArgs) =>
              `${CLOUDINARY_ROOT_PATH}/${imageTransformationParams.medium}/${path}.${ext}`
            }
            path={heroImagePath}
            ext="jpg"
            width={964}
            stretch
          />

          <StyledLargeImage
            formatPath={({ ext, path }: FormatPathArgs) =>
              `${CLOUDINARY_ROOT_PATH}/${imageTransformationParams.large}/${path}.${ext}`
            }
            path={heroImagePath}
            ext="jpg"
          />

          <StyledExtraLargeImage
            formatPath={({ ext, path }: FormatPathArgs) =>
              `${CLOUDINARY_ROOT_PATH}/${imageTransformationParams.extraLarge}/${path}.${ext}`
            }
            path={heroImagePath}
            ext="jpg"
          />
        </StyledImageGridItem>

        <StyledContentGridItem
          span={12}
          spanFromL={6}
          spanFromXL={5}
          container
          containerPaddingBottom="XXL"
          paddingFromL={0}
        >
          <H size="L" decorative>
            <PreventOrphan>{title}</PreventOrphan>
          </H>

          {subtitle && (
            <StyledSubtitle>
              <ReactMarkdown
                rehypePlugins={
                  [rehypeRaw, rehypeSanitize] as ReactMarkdown.PluggableList
                }
              >
                {subtitle}
              </ReactMarkdown>
            </StyledSubtitle>
          )}

          <Wrapper margin={['M', 0, 'S']} marginFromM={['L', 0, 'M']}>
            <LinkButton
              partnerCampaignReferral
              productLink
              to={WILLS_SIGN_UP_PATH}
              preselectedService="online"
              telephoneWillsEnabled={telephoneWillsEnabled}
            >
              {ctaText}
            </LinkButton>
          </Wrapper>

          <TrustpilotSummary />
        </StyledContentGridItem>
      </StyledGrid>
    </StyledRelativeWrapper>
  )
}

export default Hero
